/**
 * Read-only seams the five modals need but that Phase 1's operations layer
 * does not yet expose a route for: Halo candidates for a handle (Match
 * modal), the destinations of record on a Halo contact (OTP modal), a
 * client's recent tickets (Attach-ticket modal), and an AI-drafted close
 * note (Close modal).
 *
 * `services/ops` (Tasks 6-8, concurrent) defines `GET /ops/threads/:id` and
 * `POST /ops/:operation` — nothing that answers "what Halo contacts could
 * this handle be" or "what destinations does this contact have on file."
 * `ContactDirectory.destinationsOfRecord` exists as an internal dependency
 * inside services/ops/src/routes/operations.ts, used only inside the
 * request-otp gate; it is never exposed over HTTP for a caller to read
 * before sending. Same story for match candidates and an AI close-note
 * draft. Rather than fabricate this data in the component (which the brief
 * explicitly forbids for the close-note draft, and which would be exactly
 * as dishonest for the other three), this module makes the same shape of
 * real network attempt every other read in this app makes
 * (../../lib/ops.ts's `createOpsClient`, ../context/aiOps.ts's
 * `createAiOpsClient`: injectable fetch + base URL, defaulting to
 * runtime-config) against a plausible thread-scoped route, and resolves an
 * honest empty/unwired result on any non-2xx response or network error —
 * today, always, since none of these routes exist yet. The moment a later
 * task adds a real endpoint at one of these paths answering the documented
 * shape, that one method starts returning real content with no component
 * change.
 *
 * Deliberately NOT added to ../../lib/ops.ts: this task's scope is
 * apps/console/src/components/modals/** plus its own test file, and other
 * agents are concurrently building services/ops's operation set. This
 * module only reads (GET), never calls `ops.call`, so it cannot collide with
 * the operation surface they own.
 */
import { loadRuntimeConfig } from '../../runtime-config'

export interface MatchCandidate {
  contactId: string
  name: string
  company: string | null
  /** Why this contact is offered as a candidate — never "keep as alias only", which is an action
   * a dispatcher chooses per-candidate (see MatchModal), not a reason a candidate was suggested. */
  reason: 'number-on-contact' | 'shared-line' | 'other-field'
}

/** A contact the dispatcher went looking for, rather than one the number found. */
export interface ContactSearchHit {
  contactId: string
  name: string
  company: string | null
  email: string | null
  presence: { live: boolean; lastSeen: string | null } | null
}

export interface ContactSearchResult {
  wired: boolean
  results: ContactSearchHit[]
  /** True when the query was too short to run — "type more", not "nobody". */
  tooShort?: boolean
  reason?: string
}

export interface OtpDestination {
  method: 'sms' | 'email'
  /** Exactly what request-otp's `destination` argument must equal — read verbatim off the Halo
   * contact, never typed by a dispatcher or supplied by the requester. */
  destination: string
  label: string
  /**
   * What sending here would establish — see the server's `describeDestination`.
   * Surfaced beside the option rather than used to hide it: the dispatcher may
   * have a reason we cannot see, and a system that silently drops the obvious
   * choice is harder to trust than one that explains it.
   */
  strength?: 'strong' | 'circular' | 'anti-spoof'
  strengthNote?: string
}

export interface RecentTicket {
  ticket: string
  subject: string | null
}

export interface CloseDraft {
  /** True only once a real endpoint answers with an actual AI draft. Always false in Phase 1. */
  wired: boolean
  note: string
}

/** The handles Compose can actually send to for a contact — task 6's own read, backing the
 * Compose modal's channel picker. */
export interface ContactHandles {
  sms: string | null
  chat: string | null
}

/** Whether this contact's console is open right now, and when it last was — same shape as the
 * search-hit and thread-summary `presence` fields, for the same reason: "online now" and "seen
 * recently" are different claims. */
export interface ContactPresence {
  live: boolean
  lastSeen: string | null
}

/** One labeled phone number on a contact's Halo record — task 2's own read, backing the
 * Compose modal's outbound number picker. `field` is the raw Halo field name (the radio's
 * identity, and what round-trips back as the picker's selection); `label` is the friendly name
 * built from that field (see design doc's `HaloClient.contactNumbers`); `value` is the raw phone
 * value, exactly as Halo holds it — Twilio dials raw, never a display-formatted string; `isMobile`
 * flags a mobile-type field for the "pre-select the first mobile" default. */
export interface ContactNumber {
  field: string
  label: string
  value: string
  isMobile: boolean
}

export interface ContactHandlesResult {
  wired: boolean
  handles: ContactHandles
  presence: ContactPresence | null
  reason?: string
}

/** Every labeled phone number on a contact's record — task 2's own read, backing the Compose
 * modal's number picker. Same `wired` discipline as every other seam here: `wired: false` means
 * "we could not ask Halo," never "Halo said this contact has no numbers" — a genuinely
 * numberless contact is `{ wired: true, numbers: [] }`, which the composer renders as "No number
 * on file" (a real fact), distinct from the unwired case's retry-able unreachable notice. */
export interface ContactNumbersResult {
  wired: boolean
  numbers: ContactNumber[]
  reason?: string
}

/** A device the composer's Machines search found — task 7 (device-targets). Same presence shape
 * as a contact search hit (`ContactPresence`): "connected right now" vs "last seen", never
 * collapsed into one fact. `client` (device-hostname task 3) is the row's client label —
 * `'Computer Cats'` for the internal-staff sentinel, a resolved Halo client name, or `null` when
 * unresolved (absent dep, failed lookup, or no client on the row at all). Best-effort like
 * presence: a `null` client never means the device itself is unpickable. */
export interface DeviceSearchHit {
  deviceId: string
  hostname: string
  presence: ContactPresence | null
  client: string | null
}

export interface DeviceSearchResult {
  wired: boolean
  devices: DeviceSearchHit[]
  reason?: string
}

export interface ComposePrefillTicket {
  id: string
  subject: string | null
}

export interface ComposePrefillContact {
  contactId: string
  name: string
}

/**
 * Why `GET /ops/tickets/:id/compose-prefill` (thread-reads.ts) came back `wired: false`.
 *
 * A superset of `UnwiredReason` above, not that type reused verbatim: this route can also
 * answer `'not-found'` (Halo answered; this ticket id is simply not one it has), which none of
 * the other three routes' `wired: false` cases can — they are all thread-scoped reads on a
 * thread the console already knows exists, where "not found" has no meaning. Finding 4's own
 * note: these are structural codes the server sends on purpose, same discipline as
 * `UnwiredReason` — the caller keys off this, never off `reason`-as-prose.
 */
export type ComposePrefillUnwiredReason = 'halo-unreachable' | 'not-configured' | 'not-found'

/** What Compose should open pre-filled with when launched from a ticket — task 6's own read. `ticket`
 * and `contact` are only meaningful once `wired` is true; unwired always omits both rather than
 * guessing at either. */
export interface ComposePrefillResult {
  wired: boolean
  ticket?: ComposePrefillTicket
  contact?: ComposePrefillContact | null
  reason?: ComposePrefillUnwiredReason
}

/**
 * `wired` on every one of these four results distinguishes "Halo answered and this is what it
 * said" from "we could not ask Halo." That distinction is load-bearing, not cosmetic: an empty
 * `candidates: []` that looks the same whether Halo was reachable or not reads to a dispatcher as
 * "Halo has no contact matching this number" — grounds to create a duplicate contact — when the
 * truth might be "we could not ask." Same story for a contact's destinations of record (an
 * apparently-empty list would send a dispatcher to go "fix" a contact record that is actually
 * fine) and for recent tickets (an apparently-empty list invites a duplicate ticket). `wired:
 * false` must never be presented as "the search came back empty" — see each modal's distinct copy
 * for the two states.
 */
/**
 * WHY a lookup could not be answered.
 *
 * `wired: false` alone was not enough. It rendered as "Could not reach Halo" for
 * every cause, including two where Halo was answering perfectly well and the
 * real answer was an action the dispatcher could take immediately — match the
 * thread, or identify the client. Being told a third-party system is down when
 * it is not is worse than saying nothing: you go and check Halo.
 *
 *   halo-unreachable — we asked and could not get an answer. Retry.
 *   not-configured   — this deployment has no Halo integration wired at all.
 *   not-matched      — no Halo contact on this thread yet. Match it first.
 *   no-client        — no client known for this thread yet.
 */
export type UnwiredReason = 'halo-unreachable' | 'not-configured' | 'not-matched' | 'no-client'

export interface MatchCandidatesResult {
  wired: boolean
  reason?: UnwiredReason
  candidates: MatchCandidate[]
}

export interface OtpDestinationsResult {
  wired: boolean
  reason?: UnwiredReason
  destinations: OtpDestination[]
}

export interface RecentTicketsResult {
  wired: boolean
  reason?: UnwiredReason
  tickets: RecentTicket[]
}

export const CLOSE_DRAFT_SEAM: CloseDraft = { wired: false, note: '' }
export const MATCH_CANDIDATES_SEAM: MatchCandidatesResult = { wired: false, candidates: [] }
export const OTP_DESTINATIONS_SEAM: OtpDestinationsResult = { wired: false, destinations: [] }
export const RECENT_TICKETS_SEAM: RecentTicketsResult = { wired: false, tickets: [] }
export const CONTACT_HANDLES_SEAM: ContactHandlesResult = { wired: false, handles: { sms: null, chat: null }, presence: null }
export const CONTACT_NUMBERS_SEAM: ContactNumbersResult = { wired: false, numbers: [] }
export const COMPOSE_PREFILL_SEAM: ComposePrefillResult = { wired: false }

interface ModalReadsDeps {
  /** Defaults to the global `fetch`. Override in tests. */
  fetchImpl?: typeof fetch
  /** Defaults to reading /runtime-config.json via loadRuntimeConfig(). Pass a literal (or a
   * function returning one) in tests to skip that fetch entirely. */
  baseUrl?: string | (() => string | Promise<string>)
}

function arrayOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

/** Builds the modal reads client. Every method always resolves — never throws — because a missing
 * route and a genuine network failure both mean the same thing to a modal: show the honest empty
 * state, not an error banner over data that was never the point of the call. */
export function createModalReadsClient(deps: ModalReadsDeps = {}) {
  const doFetch: typeof fetch = deps.fetchImpl ?? ((...args) => fetch(...args))

  async function resolveBase(): Promise<string> {
    if (deps.baseUrl !== undefined) {
      return typeof deps.baseUrl === 'function' ? deps.baseUrl() : deps.baseUrl
    }
    return (await loadRuntimeConfig()).apiBase
  }

  async function getJson(path: string): Promise<Record<string, unknown> | null> {
    try {
      const base = await resolveBase()
      const res = await doFetch(`${base}${path}`, { credentials: 'include' })
      if (!res.ok) return null
      const body: unknown = await res.json().catch(() => null)
      return body && typeof body === 'object' ? (body as Record<string, unknown>) : null
    } catch {
      return null
    }
  }

  return {
    // Every method below requires the response to say `wired: true` before any part of it is
    // trusted — a 200 that omits `wired` is treated exactly like a 404 or a network error (not
    // wired), never as "wired, and the answer happens to be empty." That is the one rule that
    // keeps "Halo said no" and "we could not ask Halo" from ever rendering the same way.
    async matchCandidates(threadId: string): Promise<MatchCandidatesResult> {
      const body = await getJson(`/ops/threads/${encodeURIComponent(threadId)}/match-candidates`)
      if (!body || body.wired !== true) {
        // Carry the server's reason through when it gave one. A response with
        // no reason (an older build, a 404, a network failure) keeps the seam's
        // conservative default.
        const reason = typeof body?.reason === 'string' ? (body.reason as UnwiredReason) : undefined
        return reason ? { ...MATCH_CANDIDATES_SEAM, reason } : MATCH_CANDIDATES_SEAM
      }
      return { wired: true, candidates: arrayOf<MatchCandidate>(body.candidates) }
    },

    /**
     * Find a contact by name, for when the right person is not among the
     * candidates at all — the whole point on a shared main line.
     *
     * Never throws: an unreachable Halo is a `wired: false` with a reason the
     * modal shows, exactly like every other read here.
     */
    async searchContacts(query: string): Promise<ContactSearchResult> {
      const body = await getJson(`/ops/contacts/search?q=${encodeURIComponent(query)}`)
      if (!body || body.wired !== true) {
        return {
          wired: false,
          results: [],
          reason:
            typeof body?.reason === 'string'
              ? body.reason
              : 'Contacts could not be searched. Nothing has changed on this conversation.',
        }
      }
      return {
        wired: true,
        results: arrayOf<ContactSearchHit>(body.results),
        tooShort: body.tooShort === true,
      }
    },

    /**
     * Enrolled devices whose hostname matches `query` — the composer's Machines toggle (task 7).
     * Same `wired` discipline as `searchContacts` above, even though this route's own `wired:
     * false` never means "Halo is unreachable" (services/ops's `/ops/devices/search` doc comment:
     * NO Halo involvement, ever — `wired: false` here means the device repo dependency is
     * absent). The caller still must not tell those two apart from this shape alone; it just
     * shows whatever `reason` the server sent.
     */
    async searchDevices(query: string): Promise<DeviceSearchResult> {
      const body = await getJson(`/ops/devices/search?q=${encodeURIComponent(query)}`)
      if (!body || body.wired !== true) {
        return {
          wired: false,
          devices: [],
          reason:
            typeof body?.reason === 'string'
              ? body.reason
              : 'Devices could not be searched. Nothing has changed on this conversation.',
        }
      }
      return { wired: true, devices: arrayOf<DeviceSearchHit>(body.devices) }
    },

    async otpDestinations(threadId: string): Promise<OtpDestinationsResult> {
      const body = await getJson(`/ops/threads/${encodeURIComponent(threadId)}/otp-destinations`)
      if (!body || body.wired !== true) {
        // Carry the server's reason through when it gave one. A response with
        // no reason (an older build, a 404, a network failure) keeps the seam's
        // conservative default.
        const reason = typeof body?.reason === 'string' ? (body.reason as UnwiredReason) : undefined
        return reason ? { ...OTP_DESTINATIONS_SEAM, reason } : OTP_DESTINATIONS_SEAM
      }
      return { wired: true, destinations: arrayOf<OtpDestination>(body.destinations) }
    },

    async recentTickets(threadId: string): Promise<RecentTicketsResult> {
      const body = await getJson(`/ops/threads/${encodeURIComponent(threadId)}/recent-tickets`)
      if (!body || body.wired !== true) {
        // Carry the server's reason through when it gave one. A response with
        // no reason (an older build, a 404, a network failure) keeps the seam's
        // conservative default.
        const reason = typeof body?.reason === 'string' ? (body.reason as UnwiredReason) : undefined
        return reason ? { ...RECENT_TICKETS_SEAM, reason } : RECENT_TICKETS_SEAM
      }
      return { wired: true, tickets: arrayOf<RecentTicket>(body.tickets) }
    },

    /** A contact's recent tickets — same shape and same `wired`/`reason` discipline as
     * `recentTickets` above, but contact-scoped rather than thread-scoped: the composer has a
     * picked contact before it ever has a thread. Backs ComposeModal's recent-tickets list (task
     * 2). */
    async contactRecentTickets(contactId: string): Promise<RecentTicketsResult> {
      const body = await getJson(`/ops/contacts/${encodeURIComponent(contactId)}/recent-tickets`)
      if (!body || body.wired !== true) {
        // Carry the server's reason through when it gave one. A response with
        // no reason (an older build, a 404, a network failure) keeps the seam's
        // conservative default.
        const reason = typeof body?.reason === 'string' ? (body.reason as UnwiredReason) : undefined
        return reason ? { ...RECENT_TICKETS_SEAM, reason } : RECENT_TICKETS_SEAM
      }
      return { wired: true, tickets: arrayOf<RecentTicket>(body.tickets) }
    },

    async closeDraft(threadId: string): Promise<CloseDraft> {
      const body = await getJson(`/ops/threads/${encodeURIComponent(threadId)}/close-draft`)
      if (!body || body.wired !== true || typeof body.note !== 'string') return CLOSE_DRAFT_SEAM
      return { wired: true, note: body.note }
    },

    async contactHandles(contactId: string): Promise<ContactHandlesResult> {
      const body = await getJson(`/ops/contacts/${encodeURIComponent(contactId)}/handles`)
      if (!body || body.wired !== true) {
        // Carry the server's reason through when it gave one. A response with
        // no reason (an older build, a 404, a network failure) keeps the seam's
        // conservative default.
        const reason = typeof body?.reason === 'string' ? body.reason : undefined
        return reason ? { ...CONTACT_HANDLES_SEAM, reason } : CONTACT_HANDLES_SEAM
      }
      return {
        wired: true,
        handles: body.handles as ContactHandles,
        presence: (body.presence as ContactPresence | null | undefined) ?? null,
      }
    },

    /** Every labeled phone number on a contact's record — task 2's own read, backing the Compose
     * modal's outbound number picker (the fix for auto-picking a work landline: the composer now
     * lets the tech choose from ALL of the contact's numbers instead of guessing one). Same
     * `wired`/`reason` discipline as `contactHandles` above, and the same sibling route shape
     * (`GET /ops/contacts/:id/numbers` mirrors `/handles`). */
    async contactNumbers(contactId: string): Promise<ContactNumbersResult> {
      const body = await getJson(`/ops/contacts/${encodeURIComponent(contactId)}/numbers`)
      if (!body || body.wired !== true) {
        // Carry the server's reason through when it gave one. A response with
        // no reason (an older build, a 404, a network failure) keeps the seam's
        // conservative default.
        const reason = typeof body?.reason === 'string' ? body.reason : undefined
        return reason ? { ...CONTACT_NUMBERS_SEAM, reason } : CONTACT_NUMBERS_SEAM
      }
      return { wired: true, numbers: arrayOf<ContactNumber>(body.numbers) }
    },

    async composePrefill(ticketId: string): Promise<ComposePrefillResult> {
      const body = await getJson(`/ops/tickets/${encodeURIComponent(ticketId)}/compose-prefill`)
      if (!body || body.wired !== true) {
        // Carry the server's reason through when it gave one AND it is one of the codes this
        // route actually sends (thread-reads.ts's own `'not-configured' | 'halo-unreachable' |
        // 'not-found'`) — an unrecognised string (a future server change this client has not
        // caught up to) falls back to the conservative unreachable-shaped default rather than
        // widening the type to `string` to admit it.
        const raw = body?.reason
        const reason: ComposePrefillUnwiredReason | undefined =
          raw === 'not-configured' || raw === 'halo-unreachable' || raw === 'not-found' ? raw : undefined
        return reason ? { ...COMPOSE_PREFILL_SEAM, reason } : COMPOSE_PREFILL_SEAM
      }
      return {
        wired: true,
        ticket: body.ticket as ComposePrefillTicket,
        contact: (body.contact as ComposePrefillContact | null | undefined) ?? null,
      }
    },
  }
}

/** The singleton every real modal uses — same pattern as `ops` in ../../lib/ops.ts and `aiOps` in
 * ../context/aiOps.ts. */
export const modalReads = createModalReadsClient()
